<svelte:options customElement="rf-explorer" />

<script lang="ts">
	/**
	 * Imports
	 */
	import '@digirati/canvas-panel-web-components';

	/**
	 * Types
	 */
	import type { CanvasPanelWebComponent } from '@src/types/canvas-panel.d.ts';
	import type { ReframeCollection, ReframeThumbnail } from '@src/types/reframe.d.ts';
	import type { ManifestNormalized } from '@iiif/presentation-3-normalized';
	export interface ReframeExplorerProps {
		['manifest-id']: string;
	}

	/**
	 * Props
	 */
	const { 'manifest-id': manifestId = 'https://iiif.wellcomecollection.org/presentation/b18035723' }: ReframeExplorerProps = $props();

	/**
	 * Reactives
	 */
	let canvasPanel: CanvasPanelWebComponent;
	let currentId: string = $state(manifestId);
	let thumbnails: ReframeThumbnail[] = $state(new Array());
	let collections: ReframeCollection[] = $state(new Array());

	/**
	 * Methods
	 */
	const onThumbnailClick = (thumbnail: ReframeThumbnail | null = null) => {
		if (canvasPanel && thumbnail && thumbnail.canvasId) canvasPanel.setCanvas(thumbnail.canvasId);
	};

	const loadManifest = async (id: string | null = null): Promise<ManifestNormalized | undefined> => {
		try {
			if (!id) throw new Error('Manifest ID is required.');

			if (!canvasPanel) throw new Error('Canvas Panel was not initialized.');

			const manifest = await canvasPanel.vault.loadManifest(id);

			if (!manifest) throw new Error('Manifest is undefined.');

			const canvases = await Promise.all(manifest.items.map((item) => canvasPanel?.vault.get(item)));

			currentId = id;
			canvasPanel.setAttribute('canvas-id', manifest.items[0].id);
			canvasPanel.setAttribute('manifest-id', id);

			collections = manifest.partOf.map((collection: ReframeCollection) => ({
				id: collection.id,
				label: collection.label || 'Unnamed Collection',
			}));

			thumbnails = canvases.map((canvas) => ({
				canvasId: canvas?.id ?? '',
				src: canvas?.thumbnail?.[0]?.id ?? '',
			}));

			return manifest;
		} catch (error) {
			console.error('Error loading manifest:', error);

			return undefined;
		}
	};

	/**
	 * Hooks
	 */

	$effect(() => {
		loadManifest(manifestId);
	});
</script>

<section data-rf-explorer class="rf-explorer" part="rf-explorer">
	<canvas-panel class="rf-explorer__panel" bind:this={canvasPanel} move-events="true" granular-move-events="true"></canvas-panel>

	<div class="rf-explorer__thumbnails">
		{#each thumbnails as thumbnail}
			<button onclick={() => onThumbnailClick(thumbnail)}>
				<img src={thumbnail.src} alt="" srcset="" />
			</button>
		{/each}
	</div>
	<metadata-panel
		manifest-id={currentId}
		variation="table"
		label-style="small-caps"
		label-width="250"
		allow-html="true"
		bordered="false"
		separator="<br/>"
	></metadata-panel>
	<div class="rf-explorer__collection">
		{#each collections.slice(0, 3) as collection}
			<rf-collection manifestId={collection.id} manifest={(id: string) => loadManifest(id)}></rf-collection>
		{/each}
	</div>
</section>

<style lang="scss" global>
	

	.rf-explorer {
		position: relative;
		display: flex;
		flex-direction: column;
		width: 100%;

		&__panel {
			width: 100%;
		}

		&__thumbnails {
			display: flex;
			overflow-x: scroll;
		}
	}
</style>
